import { useState, useMemo, useCallback, useRef } from "react";
import { format } from "date-fns";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import fileDownload from "js-file-download";

import { Button } from "react-bootstrap";
import { BsDash, BsPlus } from "react-icons/bs";

// Services
import {
  voidOrder as voidOrderEndpoint,
  getLabelsForMultipleDeliveries,
} from "../../../../../services/deliveryService";
import { useOrderStatus } from "../../services";

// Hooks
import useDecodeToken from "../../../../../hooks/useDecodeToken";

// Constants
import { checkIfClient, queryKeys } from "../../constants";
import { accesses } from "../../../../../constants";
import { cn } from "../../../../../helpers/cn";
import handleApiError from "../../../../../helpers/handleApiError";

// Components
import { DataTable } from "../../../../DataTable";
import { OrderDetailsPanelV2NewEndpoint } from "../../../OrderDetailsPanelV2NewEndpoint";
import LoadingModal from "../../../../LoadingModal";
import { VoidConfirmationModal, InvoiceModal } from "../modals";
import {
  CodStatusColumnFilter,
  DeliveryTypeColumnFilter,
  StatusColumnFilter,
  PieceTypeFilter,
} from "../filters";
import ServerSideDeliveryMenuSelect from "../ServerSideDeliveryMenuSelect";
import PieceTypeCell from "../PieceTypeCell";
import { OrderHistoryStatusBar } from "../../../OrderHistoryStatusBar";

const defaultHiddenColumns = ["deliveryMenu"];

const renderRowSubComponent = ({ row }) => (
  <OrderDetailsPanelV2NewEndpoint
    mainOrderDataRaw={row.original}
    deliveryId={row.original.deliveryId}
    mainInvoiceNumber={row.original.invoiceNumber}
    orderCreationDate={new Date(row.original.creationDate)}
    isCod={row.original.isCod ? "Yes" : "No"}
  />
);

const renderDeliveryMenuCell = ({
  row,
  decodedToken,
  handleOnPrintLabel,
  handleOnVoidOrder,
}) => {
  const original = row?.original || {};

  return (
    <ServerSideDeliveryMenuSelect
      deliveryId={original?.deliveryId}
      linkId={original?.linkId}
      invoiceNumber={original?.invoiceNumber}
      creationDate={new Date(original.creationDate)}
      // deliveryStatus={original.status} <- Will we use different value rather than original.status
      deliveryType={original?.deliveryType}
      returnDeliveryId={original?.rtntrn}
      concernIncidentNo={original?.csrtrn}
      claimID={original?.clmtrn}
      clientDoesNotAllowCustomerReturns={original?.excluded}
      returnRequestStatuses={original?.returnRequestStatuses}
      decodedToken={decodedToken}
      onPrintLabelClick={handleOnPrintLabel}
      onVoidClick={handleOnVoidOrder}
    />
  );
};

const CXTStatusCell = ({ row, isFetchingStatus, isErrorStatus }) => {
  const deliveryId = row?.original?.deliveryId;

  const { data: statusData } = useOrderStatus(deliveryId);

  return (
    <OrderHistoryStatusBar
      deliveryId={row?.original?.deliveryId}
      status={statusData?.status}
      toggleRowExpandedProps={row?.getToggleRowExpandedProps?.()}
      isLoading={isFetchingStatus && !statusData}
      isError={isErrorStatus && !statusData}
    />
  );
};

function ServerSideDataTable({
  orders = [],
  totalRows = 0,
  isLoading = false,
  isError = false,
  isFetching = false,
  isNetworkRequestTakingTooLong = false,
  pageIndex = 0,
  pageSize = 10,
  onPaginationChange,

  columnsFilter = [],
  onColumnsFilterChange,
  columnsSorting = [],
  onColumnsSortingChange,

  isFetchingStatus = false,
  isErrorStatus = false,

  onChangePaginationMode,
  customErrorMessage,

  canFilterByStatus,
  isFetchingAllOrders,

  isEnabledAllOrders,
  maxDateRangeForStatusFilter,
}) {
  const history = useHistory();
  const [decodedToken] = useDecodeToken();
  const tableInstanceRef = useRef(null);
  const isClient = useMemo(() => checkIfClient(decodedToken), [decodedToken]);

  const queryClient = useQueryClient();

  const [isLoadingOrderInfoRequest, setIsLoadingOrderInfoRequest] =
    useState(false);

  const [showInvoiceNumberModal, setShowInvoiceNumberModal] = useState({
    isOpen: false,
    invoiceNumbers: [],
  });

  const [showVoidConfirmationModal, setShowVoidConfirmationModal] = useState({
    isOpen: false,
    voidOrdererId: null,
  });

  const handleOpenInvoiceNumberModal = useCallback((invoices) => {
    const formatInvoiceNumbers = invoices.map((invoice) => ({
      invoiceNumber: invoice?.invoiceNumber || "",
      amount: invoice?.amount ?? 0,
      insured: invoice?.isInsured ?? false,
    }));

    setShowInvoiceNumberModal({
      isOpen: true,
      invoiceNumbers: formatInvoiceNumbers,
    });
  }, []);

  const handleCloseInvoiceNumberModal = () => {
    setShowInvoiceNumberModal({ isOpen: false, invoiceNumbers: [] });
  };

  const handlePrintLabel = useCallback(
    (event) => {
      const { deliveryId } = event.target.dataset;

      if (!deliveryId) return;

      setIsLoadingOrderInfoRequest(true);

      const fetchLabelsAndDownload = async () => {
        try {
          const { data } = await getLabelsForMultipleDeliveries(
            "" + deliveryId,
            false,
            isClient,
          );

          //at this point it should prompt the user to download
          // console.log(data);
          fileDownload(data, `Label-${deliveryId}.pdf`); //For downloading, but changed it so the label would open in a new tab. Might want to make this a setting in the profile page. TODO
          // openInNewTab(`${basePath}/view-labels/${"" + deliveryId}`);
          setIsLoadingOrderInfoRequest(false);
        } catch (error) {
          setIsLoadingOrderInfoRequest(false);
          handleApiError(error, history);
        }
      };

      fetchLabelsAndDownload();
    },
    [history, isClient],
  );

  const handleShowVoidModal = useCallback((event) => {
    const { deliveryId } = event.target.dataset;

    if (!deliveryId) return;

    setShowVoidConfirmationModal({
      isOpen: true,
      voidOrdererId: deliveryId,
    });
  }, []);

  const handleCloseVoidModal = useCallback(() => {
    setShowVoidConfirmationModal({
      isOpen: false,
      voidOrdererId: null,
    });
  }, []);

  const handleConfirmationVoidOrder = useCallback(() => {
    setShowVoidConfirmationModal((prev) => ({ ...prev, isOpen: false }));

    const onCompletionORError = () => {
      setShowVoidConfirmationModal({ isOpen: false, voidOrdererId: null });
      setIsLoadingOrderInfoRequest(false);
    };

    const fetchVoidOrder = async () => {
      setIsLoadingOrderInfoRequest(true);

      try {
        await voidOrderEndpoint(showVoidConfirmationModal.voidOrdererId);

        // instead updating orders state we will just refetch the data or invalidate all queries related to order history
        queryClient.invalidateQueries({
          queryKey: queryKeys.DELIVERY_HISTORY,
        });

        queryClient.invalidateQueries({
          queryKey: queryKeys.STATUS_ORDERS,
        });

        toast.success(
          `Voided order ${showVoidConfirmationModal.voidOrdererId}`,
        );
        onCompletionORError();
      } catch (error) {
        onCompletionORError();

        handleApiError(error, history);
      }
    };

    fetchVoidOrder();
  }, [history, showVoidConfirmationModal.voidOrdererId, queryClient]);

  const columns = useMemo(
    () => [
      {
        Header: () => (decodedToken?.IsCdkClient ? "" : null),
        id: "expander", // 'id' is required when 'Header' is null
        Cell: ({ row }) => (
          <span {...row.getToggleRowExpandedProps()}>
            {row?.isExpanded ? (
              <Button
                className="order-history-expander-button"
                variant="primary"
              >
                <BsDash />
              </Button>
            ) : (
              <Button
                className="order-history-expander-button"
                variant="primary"
              >
                <BsPlus />
              </Button>
            )}
          </span>
        ),
        disableSortBy: true,
        disableFilters: true,
        width: 70,
      },
      {
        Header: "Delivery ID",
        accessor: "deliveryId",
        disableSortBy: true,
        sortDescFirst: true,
        width: 110,
      },
      {
        Header: ({ state, setFilter }) => {
          const { filters } = state;
          // this is custom filter that is not used by columns directly
          // insuredFilterValue = "Yes" | "No" | undefined
          const insuredFilterValue = filters.find(
            (f) => f?.id === "insuredFilter",
          )?.value;

          return (
            <div className="tw-flex tw-justify-between tw-items-center">
              <div>Invoice #'s</div>
              {isClient && (
                <div>
                  <div
                    role="button"
                    type="button"
                    className={cn("order-history-invoice-insured-button", {
                      "order-history-invoice-insured-button-toggled-on":
                        insuredFilterValue === "Yes",
                    })}
                    onClick={() => {
                      setFilter(
                        "insuredFilter",
                        insuredFilterValue === "Yes" ? "No" : "Yes",
                      );
                    }}
                  >
                    Insured
                  </div>
                </div>
              )}
            </div>
          );
        },

        accessor: "invoices",
        disableSortBy: true,
        width: 215,
        Cell: ({ value }) => {
          const invoiceNumbers = value || [];

          const amountLimitToShow = 5;
          const invoicesToShow = invoiceNumbers.slice(0, amountLimitToShow);
          const remainingInvoices =
            invoiceNumbers.length - invoicesToShow.length;
          return (
            <div
              className="clickable no-copy h-100"
              role="button"
              onClick={() => {
                handleOpenInvoiceNumberModal(invoiceNumbers);
              }}
            >
              {invoicesToShow.map((invoice, index) => (
                <div
                  key={index + "-" + invoice.invoiceNumber}
                  className={cn("order-history-invoice-chip", {
                    "order-invoice-history-chip-highlighted":
                      isClient && invoice.isInsured,
                  })}
                >
                  {invoice.invoiceNumber}
                </div>
              ))}

              {remainingInvoices > 0 && (
                <div
                  className="order-history-invoice-chip order-history-invoice-chip-see-more"
                  role="button"
                >
                  + {remainingInvoices} more
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: "Reference #",
        accessor: "refNo",
        disableSortBy: true,
        width: 120,
      },
      {
        accessor: "insuredFilter",
        id: "insuredFilter",
        disableFilters: true,
        disableSortBy: true,
        Cell: () => null,
      },
      {
        Header: "Delivery Date",
        // accessor: "deliveryDate",
        accessor: (value) => new Date(value.deliveryDate),
        id: "deliveryDate",
        Cell: ({ value }) => format(value, "MM/dd/yyyy"),
        disableFilters: true,
        sortType: "datetime",
        width: 90,
      },
      {
        Header: "Creation Date",
        // accessor: "creationDate",
        accessor: (value) => new Date(value.creationDate),
        id: "creationDate",
        Cell: ({ value }) => `${format(value, "MM/dd/yyyy hh:mma")} CST`,
        disableFilters: true,
        sortType: "datetime",
        width: 90,
      },
      {
        Header: isClient ? "Customer" : "Dealer",
        accessor: (val) =>
          `${val.customerName}\n${val.customerAddress}\n${val.customerCity}, ${val.customerState} ${val.customerZip}\n${val.customerPhoneNumber}`,
        id: "customerInfo",
        Cell: ({ value }) => {
          const arr = value.split("\n");
          return (
            <span>
              {arr[0]}
              <br />
              {arr[1]} {arr[2]}
            </span>
          );
        },
        disableSortBy: true,
        width: 300,
      },
      {
        Header: "Route",
        accessor: "route",
        disableSortBy: true,
        width: 100,
      },
      {
        Header: "Pieces",
        accessor: "pieceCount",
        disableFilters: true,
        width: 60,
      },
      {
        Header: "Piece Type",
        accessor: "pieceType",
        disableSortBy: true,
        width: 100,
        Filter: ({ column }) => <PieceTypeFilter column={column} />,
        Cell: ({ value }) => <PieceTypeCell value={value} />,
      },
      {
        Header: "COD",
        accessor: "codStatus",
        disableSortBy: true,
        width: 90,
        Filter: ({ column }) => <CodStatusColumnFilter column={column} />,
      },
      {
        Header: "Amount",
        accessor: "totalAmount",
        Cell: ({ value }) =>
          value?.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
        sortType: "number",
        width: 80,
        disableFilters: true,
      },
      {
        Header: "Delivery Type",
        accessor: "deliveryType",
        disableSortBy: true,
        width: 115,
        Filter: ({ column }) => <DeliveryTypeColumnFilter column={column} />,
      },
      {
        Header: "Status",
        accessor: "status",
        disableSortBy: true,
        width: 180,
        Filter: ({ column }) => (
          <StatusColumnFilter
            switchToFetchAllMode={onChangePaginationMode}
            column={column}
            isClientSideRendering={false}
            canFilterByStatus={canFilterByStatus}
            isFetchingAllOrders={isFetchingAllOrders}
            isEnabledAllOrders={isEnabledAllOrders}
            maxDateRangeForStatusFilter={maxDateRangeForStatusFilter}
          />
        ),
        Cell: ({ row }) => (
          <CXTStatusCell
            row={row}
            isFetchingStatus={isFetchingStatus}
            isErrorStatus={isErrorStatus}
          />
        ),
      },
      {
        Header: () => <div style={{ textAlign: "center" }}>Options</div>,
        accessor: "deliveryMenu",
        Cell: ({ row }) =>
          renderDeliveryMenuCell({
            row,
            decodedToken,
            handleOnPrintLabel: handlePrintLabel,
            handleOnVoidOrder: handleShowVoidModal,
          }),
        disableFilters: true,
        disableSortBy: true,
        width: 80,
      },
    ],
    [
      decodedToken,
      handleOpenInvoiceNumberModal,
      handlePrintLabel,
      handleShowVoidModal,
      isClient,
      isFetchingStatus,
      isErrorStatus,
      onChangePaginationMode,
      canFilterByStatus,
      isFetchingAllOrders,
    ],
  );

  return (
    <>
      <InvoiceModal
        show={showInvoiceNumberModal.isOpen}
        handleCloseModal={handleCloseInvoiceNumberModal}
        invoices={showInvoiceNumberModal.invoiceNumbers}
        isClient={isClient}
      />

      <VoidConfirmationModal
        show={showVoidConfirmationModal.isOpen}
        onClose={handleCloseVoidModal}
        onYesClick={handleConfirmationVoidOrder}
      />

      <LoadingModal active={isLoadingOrderInfoRequest} />

      <DataTable
        columns={columns}
        data={orders}
        isLoading={isLoading}
        isError={isError}
        customErrorMessage={customErrorMessage}
        isFetching={isFetching}
        isNetworkRequestTakingTooLong={isNetworkRequestTakingTooLong}
        initialState={{
          pageIndex,
          pageSize,
          filters: columnsFilter,
          sortBy: columnsSorting,
          hiddenColumns:
            !isClient ||
            decodedToken?.[accesses.Deltrans] ||
            decodedToken?.[accesses.IsMainAccount]
              ? ["insuredFilter"]
              : ["insuredFilter", ...defaultHiddenColumns],
        }}
        slotProps={{
          topToolbar: {
            disabled: true,
          },
        }}
        ref={tableInstanceRef}
        renderDetailPanel={renderRowSubComponent}
        // Server-side state management: manualSorting, manualFilters, and manualPagination required
        manualPagination
        manualFilters
        manualSorting
        totalRows={totalRows}
        pageCount={Math.ceil(totalRows / pageSize)}
        onPaginationChange={onPaginationChange}
        onColumnFiltersChange={onColumnsFilterChange}
        onSortingChange={onColumnsSortingChange}
      />
    </>
  );
}

export default ServerSideDataTable;
